import * as React from 'react'
import { cn } from '@/lib/utils'

export interface SwitchProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'onChange'> {
  onCheckedChange?: (checked: boolean) => void
}

/**
 * An on/off toggle.
 *
 * Built on a native checkbox with role="switch" rather than a new dependency,
 * matching how Checkbox is done in this folder. That keeps it keyboard- and
 * screen-reader-operable for free: Space and Enter work, and the state is
 * announced as on/off rather than checked/unchecked.
 *
 * The knob position shows the CURRENT state — always pair it with a visible
 * label saying what that state means, never leave a bare toggle to be read as
 * "the action this will perform".
 */
const Switch = React.forwardRef<HTMLInputElement, SwitchProps>(
  ({ className, checked, disabled, onCheckedChange, ...props }, ref) => (
    <label
      className={cn(
        'relative inline-flex shrink-0 items-center',
        disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
      )}
    >
      <input
        type="checkbox"
        role="switch"
        ref={ref}
        checked={checked}
        disabled={disabled}
        aria-checked={checked}
        className="peer sr-only"
        onChange={(e) => onCheckedChange?.(e.target.checked)}
        {...props}
      />
      <span
        className={cn(
          'h-6 w-11 rounded-full border-2 border-transparent bg-input transition-colors',
          'peer-focus-visible:outline-none peer-focus-visible:ring-2',
          'peer-focus-visible:ring-ring peer-focus-visible:ring-offset-2',
          'peer-checked:bg-primary',
          className,
        )}
      />
      <span
        aria-hidden
        className={cn(
          'pointer-events-none absolute left-0.5 h-5 w-5 rounded-full bg-white shadow',
          'transition-transform peer-checked:translate-x-5',
        )}
      />
    </label>
  ),
)
Switch.displayName = 'Switch'

export { Switch }
